import { useTranslation } from 'react-i18next';
import { BED_DISPLAY_DECIMALS } from '../../../constants/capacityConstants';
import { BedOverbookingSection } from './BedOverbookingSection';
import { DistributionBarRow } from './DistributionBarRow';
import { DistributionLegend } from './DistributionLegend';
import type { WeekKpi } from './types';
import styles from './KpiDistributionPopover.module.css';

interface BedDistributionPopoverProps {
  kpi: WeekKpi;
}

// The week's beds: every admitted seat against every room, then each room
// property's booked demand against the rooms offering it. The bars carry bare
// counts: a single-patient bar is too short for a word, and the legend names
// the unit.
export const BedDistributionPopover = ({ kpi }: BedDistributionPopoverProps) => {
  const { t } = useTranslation();
  const needs = kpi.roomNeeds?.needs ?? [];
  const overbookedNeeds = needs.filter((need) => need.affectedPatients.length > 0);
  // Needs that each fit on their own can still collide over the same rooms.
  // Only named when no single property explains it, or it would repeat them.
  const conflictingPatients =
    overbookedNeeds.length === 0 ? (kpi.roomNeeds?.conflictingPatients ?? []) : [];

  return (
    <div className={styles.popover}>
      <div className={styles.title}>{t('gantt-bed-distribution-title')}</div>
      <div className={styles.grid}>
        <DistributionBarRow
          name={t('gantt-beds-all')}
          planned={kpi.bedsCount}
          threshold={kpi.bedsThreshold}
          plannedLabel={String(kpi.bedsCount)}
          thresholdLabel={
            kpi.bedsThreshold && t('gantt-beds-rooms', { count: kpi.bedsThreshold.limit })
          }
          deltaDecimals={BED_DISPLAY_DECIMALS}
        />
        {needs.map((need) => (
          <DistributionBarRow
            key={need.propertyId}
            name={need.name}
            planned={need.needed}
            threshold={{ limit: need.available }}
            plannedLabel={String(need.needed)}
            thresholdLabel={t('gantt-beds-rooms', { count: need.available })}
            deltaDecimals={BED_DISPLAY_DECIMALS}
          />
        ))}
      </div>
      <DistributionLegend
        plannedLabel={t('gantt-beds-legend-needed')}
        availableLabel={t('gantt-beds-legend-available')}
      />
      {overbookedNeeds.map((need) => (
        <BedOverbookingSection
          key={need.propertyId}
          title={t('gantt-beds-property-overbooked', { name: need.name })}
          patients={need.affectedPatients}
        />
      ))}
      {conflictingPatients.length > 0 && (
        <BedOverbookingSection
          title={t('gantt-beds-needs-conflict')}
          patients={conflictingPatients}
        />
      )}
    </div>
  );
};
